import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trackEvent } from '@/utils/analytics';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useFormAutosave } from '@/hooks/useFormAutosave';
import { reportFailedSubmission } from '@/hooks/useFormFailureBeacon';
import { RestoreDraftBanner } from '@/components/shared/RestoreDraftBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

export const IndustryQuoteModal = ({ isOpen, onClose, industry = null }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    industry: industry?.title || '',
    projectType: '',
    squareFootage: '',
    timeframe: '',
    budgetRange: '',
    additionalServices: [],
    projectDescription: '',
    subscribeUpdates: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Autosave: persist progress so the customer can recover after a network
  // failure or accidental close. Cleared when they reach the success state.
  const {
    hasDraft, draftAge, restoreDraft, clearDraft, dismissDraftBanner,
  } = useFormAutosave('industry-quote', formData, setFormData, { enabled: !isSuccess });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.company.trim()) newErrors.company = 'Company name is required';
    if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job title is required';
    if (!formData.industry) newErrors.industry = 'Please select an industry';
    if (!formData.projectType) newErrors.projectType = 'Please select a project type';
    if (!formData.squareFootage.trim()) newErrors.squareFootage = 'Square footage is required';
    if (!formData.timeframe) newErrors.timeframe = 'Please select a project timeline';
    if (!formData.budgetRange) newErrors.budgetRange = 'Please select a budget range';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      const res = await fetch(`/api/quote-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          projectType: formData.projectType,
          squareFootage: formData.squareFootage,
          timeline: formData.timeframe,
          budget: formData.budgetRange,
          message: formData.projectDescription,
          industry: formData.industry,
        }),
      });
      if (!res.ok) throw new Error('Submit failed');
      trackEvent('quote_submitted', {
        form_type: 'industry_quote',
        industry: formData.industry || 'unspecified',
        project_type: formData.projectType || 'unspecified',
      });
      clearDraft();
      setIsSuccess(true);
    } catch (err) {
      console.error('Quote submission error:', err);
      reportFailedSubmission({
        formType: 'quote',
        draft: formData,
        errorReason: err?.message || 'fetch_failed',
      });
      setErrors(prev => ({ ...prev, submit: "We couldn't submit your quote request. Please try again, or email sales@porticosystems.com directly." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      additionalServices: prev.additionalServices.includes(service)
        ? prev.additionalServices.filter(s => s !== service)
        : [...prev.additionalServices, service]
    }));
  };

  const handleClose = () => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      company: '',
      jobTitle: '',
      industry: '',
      projectType: '',
      squareFootage: '',
      timeframe: '',
      budgetRange: '',
      additionalServices: [],
      projectDescription: '',
      subscribeUpdates: false
    });
    setErrors({});
    setIsSuccess(false);
    onClose();
  };

  // Update industry when modal opens with different industry
  React.useEffect(() => {
    if (industry) {
      setFormData(prev => ({ ...prev, industry: industry.title }));
    }
  }, [industry]);

  const additionalServiceOptions = [
    'Design Consultation',
    'Site Survey',
    'Existing Flooring Removal',
    'Professional Installation',
    'Maintenance Program',
    'Custom Logo Matting'
  ];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="industry-quote-modal">
        {/* Header */}
        <div className="bg-primary text-primary-foreground px-6 py-5">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              Request Your Custom Quote
            </DialogTitle>
            <DialogDescription className="text-sm text-primary-foreground/90 mt-1">
              {industry 
                ? `Get a tailored flooring solution for ${industry.title}`
                : 'Tell us about your project and we\'ll provide a detailed estimate'
              }
            </DialogDescription>
          </DialogHeader>
        </div>

        {isSuccess ? (
          /* Success State */
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h3 className="font-heading font-semibold text-xl mb-2">
              Quote Request Submitted!
            </h3>
            <p className="text-muted-foreground mb-6">
              Thank you for your interest! Our team will review your project details and get back to you within 24 hours with a customized quote.
            </p>
            <Button onClick={handleClose} data-testid="close-success-btn">
              Close
            </Button>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <RestoreDraftBanner
              show={hasDraft}
              draftAge={draftAge}
              onRestore={restoreDraft}
              onDismiss={dismissDraftBanner}
              testId="quote-restore-draft"
            />
            {/* Value Props */}
            <div className="grid grid-cols-3 gap-3 mb-2">
              <div className="text-center p-3 bg-muted rounded-lg">
                <p className="font-semibold text-xs sm:text-sm">Detailed Estimate</p>
              </div>
              <div className="text-center p-3 bg-muted rounded-lg">
                <p className="font-semibold text-xs sm:text-sm">24hr Response</p>
              </div>
              <div className="text-center p-3 bg-muted rounded-lg">
                <p className="font-semibold text-xs sm:text-sm">Expert Consultation</p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input
                    id="fullName"
                    data-testid="quote-fullname-input"
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    placeholder="John Doe"
                    className={errors.fullName ? 'border-destructive' : ''}
                  />
                  {errors.fullName && (
                    <p className="text-xs text-destructive">{errors.fullName}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="quote-email-input"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="john@company.com"
                    className={errors.email ? 'border-destructive' : ''}
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive">{errors.email}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="quote-phone-input"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className={errors.phone ? 'border-destructive' : ''}
                  />
                  {errors.phone && (
                    <p className="text-xs text-destructive">{errors.phone}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company/Organization *</Label>
                  <Input
                    id="company"
                    data-testid="quote-company-input"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    placeholder="Acme Corporation"
                    className={errors.company ? 'border-destructive' : ''}
                  />
                  {errors.company && (
                    <p className="text-xs text-destructive">{errors.company}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="jobTitle">Job Title *</Label>
                  <Input
                    id="jobTitle"
                    data-testid="quote-jobtitle-input"
                    value={formData.jobTitle}
                    onChange={(e) => handleChange('jobTitle', e.target.value)}
                    placeholder="Facilities Manager"
                    className={errors.jobTitle ? 'border-destructive' : ''}
                  />
                  {errors.jobTitle && (
                    <p className="text-xs text-destructive">{errors.jobTitle}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry *</Label>
                  <Select 
                    value={formData.industry}
                    onValueChange={(v) => handleChange('industry', v)}
                  >
                    <SelectTrigger data-testid="quote-industry-select" className={errors.industry ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Corporate Offices">Corporate Offices</SelectItem>
                      <SelectItem value="Grocery & Convenience">Grocery & Convenience</SelectItem>
                      <SelectItem value="Retailers">Retailers</SelectItem>
                      <SelectItem value="Hospitality Sector">Hospitality Sector</SelectItem>
                      <SelectItem value="Education">Education</SelectItem>
                      <SelectItem value="Healthcare">Healthcare</SelectItem>
                      <SelectItem value="Financial Institutions">Financial Institutions</SelectItem>
                      <SelectItem value="Government">Government</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.industry && (
                    <p className="text-xs text-destructive">{errors.industry}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-4">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Project Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="projectType">Project Type *</Label>
                  <Select 
                    value={formData.projectType}
                    onValueChange={(v) => handleChange('projectType', v)}
                  >
                    <SelectTrigger data-testid="quote-projecttype-select" className={errors.projectType ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select project type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new-construction">New Construction</SelectItem>
                      <SelectItem value="renovation">Renovation/Remodel</SelectItem>
                      <SelectItem value="replacement">Replacement/Repair</SelectItem>
                      <SelectItem value="maintenance">Maintenance/Upkeep</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.projectType && (
                    <p className="text-xs text-destructive">{errors.projectType}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="squareFootage">Square Footage *</Label>
                  <Input
                    id="squareFootage"
                    data-testid="quote-sqft-input"
                    value={formData.squareFootage}
                    onChange={(e) => handleChange('squareFootage', e.target.value)}
                    placeholder="e.g., 10,000"
                    className={errors.squareFootage ? 'border-destructive' : ''}
                  />
                  {errors.squareFootage && (
                    <p className="text-xs text-destructive">{errors.squareFootage}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timeframe">Project Timeline *</Label>
                  <Select 
                    value={formData.timeframe}
                    onValueChange={(v) => handleChange('timeframe', v)}
                  >
                    <SelectTrigger data-testid="quote-timeframe-select" className={errors.timeframe ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select timeframe" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asap">ASAP</SelectItem>
                      <SelectItem value="1-month">Within 1 Month</SelectItem>
                      <SelectItem value="2-3-months">2-3 Months</SelectItem>
                      <SelectItem value="3-6-months">3-6 Months</SelectItem>
                      <SelectItem value="6-plus">6+ Months</SelectItem>
                      <SelectItem value="planning">Just Planning</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.timeframe && (
                    <p className="text-xs text-destructive">{errors.timeframe}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budgetRange">Budget Range *</Label>
                  <Select 
                    value={formData.budgetRange}
                    onValueChange={(v) => handleChange('budgetRange', v)}
                  >
                    <SelectTrigger data-testid="quote-budget-select" className={errors.budgetRange ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under-10k">Under $10,000</SelectItem>
                      <SelectItem value="10k-25k">$10,000 - $25,000</SelectItem>
                      <SelectItem value="25k-50k">$25,000 - $50,000</SelectItem>
                      <SelectItem value="50k-100k">$50,000 - $100,000</SelectItem>
                      <SelectItem value="100k-plus">$100,000+</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.budgetRange && (
                    <p className="text-xs text-destructive">{errors.budgetRange}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Services */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Additional Services (Optional)</h4>
              <div className="grid grid-cols-2 gap-2">
                {additionalServiceOptions.map((service) => (
                  <div key={service} className="flex items-center gap-2">
                    <Checkbox
                      id={`service-${service}`}
                      checked={formData.additionalServices.includes(service)}
                      onCheckedChange={() => handleServiceToggle(service)}
                    />
                    <Label 
                      htmlFor={`service-${service}`} 
                      className="text-sm font-normal cursor-pointer"
                    >
                      {service}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Project Description */}
            <div className="space-y-2">
              <Label htmlFor="projectDescription">Project Description</Label>
              <Textarea 
                id="projectDescription"
                data-testid="quote-description-textarea"
                value={formData.projectDescription}
                onChange={(e) => handleChange('projectDescription', e.target.value)}
                placeholder="Tell us more about your project requirements, specific challenges, or any special considerations..."
                rows={3}
              />
            </div>

            {/* Subscribe */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="subscribeUpdates"
                checked={formData.subscribeUpdates}
                onCheckedChange={(checked) => handleChange('subscribeUpdates', checked)}
              />
              <Label htmlFor="subscribeUpdates" className="text-sm font-normal cursor-pointer">
                Keep me updated on industry insights and new product releases
              </Label>
            </div>

            {/* Submit */}
            <div className="pt-2">
              {errors.submit && (
                <p className="text-sm text-red-500 bg-red-50 p-3 rounded-lg mb-3" data-testid="quote-submit-error">{errors.submit}</p>
              )}
              <Button 
                type="submit" 
                className="w-full"
                disabled={isSubmitting}
                data-testid="quote-submit-btn"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              By submitting this form, you agree to our{' '}
              <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IndustryQuoteModal;
